import React, { useEffect } from 'react';
import type { UseFormRegister, UseFormWatch, UseFormSetValue } from 'react-hook-form';
import type { PurchaseOrderFormData } from '../../types/purchaseOrder';
import type { FieldErrors } from 'react-hook-form';
import { openNativeDatePickerSafely, preventManualDateEntry, preventManualDatePasteOrDrop } from '../../utils/date';
import { useLanguage } from '../../features/language/useLanguage';

interface Props {
  register: UseFormRegister<PurchaseOrderFormData>;
  watch: UseFormWatch<PurchaseOrderFormData>;
  setValue: UseFormSetValue<PurchaseOrderFormData>;
  errors?: FieldErrors<PurchaseOrderFormData>;
  initialStatus?: string;
  readOnly?: boolean;
}

export const PODeliveryDetails: React.FC<Props> = ({ register, watch, setValue, errors, initialStatus, readOnly = false }) => {
  const { t } = useLanguage();
  const deliveryStatus = watch('deliveryStatus');
  const actualDeliveryDate = watch('actualDeliveryDate');
  const orderDate = watch('orderDate');

  useEffect(() => {
    if (deliveryStatus === 'received' && !actualDeliveryDate) {
      setValue('actualDeliveryDate', new Date().toISOString().split('T')[0]);
    }
  }, [deliveryStatus, actualDeliveryDate, setValue]);

  useEffect(() => {
    if (deliveryStatus !== 'received' || !orderDate || !actualDeliveryDate) return;

    if (actualDeliveryDate < orderDate) {
      setValue('actualDeliveryDate', orderDate, { shouldValidate: true, shouldDirty: true });
    }
  }, [deliveryStatus, orderDate, actualDeliveryDate, setValue]);

  return (
    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 h-full">
      <h3 className="text-lg font-medium text-gray-900 mb-4 border-b pb-2">{t('purchaseOrder.deliveryDetails.title')}</h3>
      <div className="space-y-4">
        {deliveryStatus === 'received' && (
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('purchaseOrder.deliveryDetails.actualDeliveryDate')}</label>
            <input
              data-test-id="po-create-actual-delivery"
              type="date"
              {...register('actualDeliveryDate')}
              min={orderDate || undefined}
              onClick={openNativeDatePickerSafely}
              onKeyDown={preventManualDateEntry}
              onPaste={preventManualDatePasteOrDrop}
              disabled={readOnly}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 disabled:bg-gray-100 disabled:text-gray-500"
            />
            {errors?.actualDeliveryDate && <p className="mt-1 text-sm text-red-600">{errors.actualDeliveryDate.message}</p>}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700">{t('purchaseOrder.deliveryDetails.deliveryStatus')}</label>
          <select
            data-test-id="po-create-delivery-status"
            {...register('deliveryStatus')}
            disabled={initialStatus === 'received' || readOnly}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 disabled:bg-gray-100 disabled:text-gray-500"
          >
            <option value="pending">{t('purchaseOrder.filters.options.pending')}</option>
            <option value="shipping">{t('purchaseOrder.filters.options.shipping')}</option>
            <option value="received">{t('purchaseOrder.filters.options.received')}</option>
          </select>
        </div>
      </div>
    </div>
  );
};
